import { useCallback } from 'react';
import { useClickOutside } from 'hooks';
import { SELECT_TEXTS } from '../../select.constants';
import { useSelectContext } from '../../context';
import { Indicators } from './components';
import {
  ChipsWrapper,
  Placeholder,
  RequiredMark,
  SearchInput,
  StyledTrigger,
} from './select-trigger.styled';
import { Skeleton } from '../../../../skeleton';
import { Chip } from '../../../chip';
import { Text } from '../../../text';

export const SelectTrigger = () => {
  const { refs, state, actions } = useSelectContext();

  useClickOutside(refs.triggerRef, () => actions.closeDropdown(), [
    refs.dropdownRef,
  ]);

  const handleTriggerClick = useCallback(() => {
    if (!state.fullDisabled) {
      refs.searchRef.current?.focus();
      actions.toggleDropdown();
    }
  }, [state.fullDisabled]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      actions.setSearch(e.target.value);
      actions.highlight(0);

      if (!state.isOpen) {
        actions.openDropdown();
      }
    },
    [state.isOpen]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && state.search.length === 0 && state.multi) {
        actions.removeLastOption();
      }
    },
    [state.search, state.multi, state.selectedOptions]
  );

  const placeholderText = state.readOnly
    ? SELECT_TEXTS.readonly
    : !state.multi && state.value
      ? state.options.find((el) => el.value === state.value)?.label
      : (state.placeholder ?? SELECT_TEXTS.placeholder);

  const showRequiredPlaceholderMark =
    !!state.requiredPlaceholderMark &&
    !state.multi &&
    !state.selectedOptions.length &&
    !state.isOpen &&
    !state.search.length;

  return (
    <StyledTrigger
      ref={refs.triggerRef}
      onClick={handleTriggerClick}
      data-loading={state.loading}
    >
      {state.loading && <Skeleton overlay />}

      {/* Пропс multi нужен, чтобы при фокусе на инпут скрыть остальные элементы */}
      <ChipsWrapper $multi={!!state.multi}>
        {state.loading && typeof state.loadingText !== 'boolean' ? (
          <Text color="gray.400">
            {state.loadingText ?? SELECT_TEXTS.loading}
          </Text>
        ) : (
          <>
            {/* TODO: упростить условие отображения селектора с выбранными
            вариантами: зависит от состояния state.multi и state.isOpen 
            если селектор state.multi true, мы отображаем Chips, 
            при открытии дропдауна, выбранные Chips не пропадают в селекторе, 
            и иначе для состояния state.multi false: при открытии дропдауна, текст не отображаем, 
            вместо него плейсхолдер с текстом выбранного варианта 
            */}
            {(state.multi || (!state.multi && !state.isOpen)) &&
              state.selectedOptions.map((option) =>
                state.multi ? (
                  <Chip
                    key={option.value}
                    onRemove={() => actions.remove(option.value)}
                    {...option}
                  />
                ) : (
                  <Text key={option.value}>{option.label}</Text>
                )
              )}

            <Placeholder
              className={
                state.responsive.viewport.mobile ? 'body-16' : 'body-14'
              }
              $show={showRequiredPlaceholderMark}
            >
              {placeholderText}
              <RequiredMark>*</RequiredMark>
            </Placeholder>

            <SearchInput
              ref={refs.searchRef}
              className={
                state.responsive.viewport.mobile ? 'body-16' : 'body-14'
              }
              $show={state.isOpen || !state.selectedOptions.length}
              type={'text'}
              readOnly={!state.searchable}
              placeholder={showRequiredPlaceholderMark ? '' : placeholderText}
              value={state.search}
              onChange={handleSearch}
              onKeyDown={handleKeyDown}
              disabled={state.fullDisabled}
            />
          </>
        )}
      </ChipsWrapper>

      <Indicators />
    </StyledTrigger>
  );
};
